import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Get_dispute_details, Resolve_dispute, MediaUrl } from '../../../Utils/Api'; // Check MediaUrl import if used
import { toast } from 'react-toastify';

const DisputeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dispute, setDispute] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resolving, setResolving] = useState(false);
    const [adminNotes, setAdminNotes] = useState("");
    const [showPartialModal, setShowPartialModal] = useState(false);
    const [partialDetails, setPartialDetails] = useState(null);
    const [partialAmount, setPartialAmount] = useState("");

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await Get_dispute_details(id);
            if (response?.data?.success) {
                setDispute(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
            toast.error("Failed to fetch dispute details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const handleResolve = async (decision, overrideAmount = null) => {
        if (!overrideAmount && !window.confirm(`Are you sure you want to ${decision}? This action cannot be undone.`)) return;

        setResolving(true);
        try {
            const payload = {
                decision, // "Process Escrow" or "Deny Escrow" or "Process Partial Escrow"
                adminNotes,
                partialAmount: overrideAmount
            };
            const response = await Resolve_dispute(id, payload);
            if (response?.status === 200 || response?.data?.success) {
                toast.success(`Dispute resolved: ${decision}`);
                setShowPartialModal(false);
                fetchDetails(); // Refresh
            }
        } catch (error) {
            console.error("Error resolving:", error);
            if (error.response?.data?.message) {
                try {
                    // Backend sends stringified JSON for specific errors
                    const errObj = JSON.parse(error.response.data.message);
                    if (errObj.code === 'INSUFFICIENT_WALLET') {
                        setPartialDetails(errObj);
                        setShowPartialModal(true);
                        setResolving(false);
                        return;
                    }
                } catch(e) {}
            }
            toast.error(error.response?.data?.message || "Failed to resolve dispute");
        } finally {
            setResolving(false);
        }
    };

    if (loading) return <div className="p-4 text-center">Loading...</div>;
    if (!dispute) return <div className="p-4 text-center">Dispute not found</div>;

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Dispute Details</h4>
                <button onClick={() => navigate(-1)} className="btn btn-outline-secondary btn-sm">Back</button>
            </div>

            <div className="row">
                <div className="col-md-8">
                    {/* Dispute Information */}
                    <div className="card mb-4">
                        <div className="card-header bg-danger text-white">
                            Dispute Filed By: {dispute.raisedBy?.name}
                        </div>
                        <div className="card-body">
                            <p><strong>Reason:</strong> {dispute.reason}</p>
                            <p><strong>Description:</strong></p>
                            <div className="bg-light p-3 rounded">{dispute.description}</div>

                            {dispute.evidence?.length > 0 && (
                                <div className="mt-3">
                                    <strong>Evidence:</strong>
                                    <div className="d-flex gap-2 mt-2 flex-wrap">
                                        {dispute.evidence.map((url, i) => (
                                            <a key={i} href={MediaUrl + url.replace(/\\/g, '/')} target="_blank" rel="noreferrer" className="border p-1 rounded">
                                                Evidence {i + 1}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Response Information */}
                    {dispute.response?.submittedAt ? (
                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                Response from: {dispute.raisedAgainst?.name}
                            </div>
                            <div className="card-body">
                                <p><strong>Description:</strong></p>
                                <div className="bg-light p-3 rounded">{dispute.response.description}</div>

                                {dispute.response.evidence?.length > 0 && (
                                    <div className="mt-3">
                                        <strong>Evidence:</strong>
                                        <div className="d-flex gap-2 mt-2 flex-wrap">
                                            {dispute.response.evidence.map((url, i) => (
                                                <a key={i} href={MediaUrl + url.replace(/\\/g, '/')} target="_blank" rel="noreferrer" className="border p-1 rounded">
                                                    Evidence {i + 1}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-warning">
                            The other party has not responded yet.
                        </div>
                    )}
                </div>

                <div className="col-md-4">
                    {/* Gig Details Card */}
                    {dispute.contractId?.gigId && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="card-title">Gig Details</h5>
                                <p className="mb-1"><strong>Title:</strong> {dispute.contractId.gigId.title}</p>
                                <p className="mb-1"><strong>Budget:</strong> ${dispute.contractId.budget}</p>
                                <p className="mb-0"><strong>Status:</strong> {dispute.contractId.status === 2 ? "Paused/Hold" : dispute.contractId.status}</p>
                            </div>
                        </div>
                    )}

                    {/* Status & Actions */}
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">Resolution</h5>
                            <p>Current Status: <strong>{dispute.status}</strong></p>

                            {dispute.status !== 'Resolved' && dispute.status !== 'Closed' ? (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label">Admin Notes</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                            placeholder="Enter reason for decision..."
                                        ></textarea>
                                    </div>
                                    <div className="d-grid gap-2">
                                        <button
                                            className="btn btn-success"
                                            onClick={() => handleResolve('Process Escrow')}
                                            disabled={resolving}
                                        >
                                            Process Escrow (Pay Skillster)
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => handleResolve('Cancel Gig')}
                                            disabled={resolving}
                                        >
                                            Deny Escrow (Cancel Gig)
                                        </button>
                                    </div>
                                    <p className="text-muted small mt-2">
                                        * "Process Escrow" will complete the gig and release funds to Skillster.<br />
                                        * "Deny Escrow" will cancel the gig.
                                    </p>
                                </>
                            ) : (
                                <div className="alert alert-success">
                                    Resolved by Admin on {new Date(dispute.resolution?.resolvedAt).toLocaleDateString()}
                                    <br />
                                    <strong>Decision:</strong> {dispute.resolution?.decision}
                                    <br />
                                    <strong>Notes:</strong> {dispute.resolution?.adminNotes}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Partial Payment Modal */}
            {showPartialModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-warning">
                                <h5 className="modal-title">Insufficient Gigster Wallet</h5>
                                <button type="button" className="btn-close" onClick={() => setShowPartialModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-danger">
                                    The Gigster only has <strong>${partialDetails?.available ? (partialDetails.available / 100).toFixed(2) : '0.00'}</strong> available in their wallet.
                                    <br/>
                                    However, <strong>${partialDetails?.required ? (partialDetails.required / 100).toFixed(2) : '0.00'}</strong> is required to fully pay the Skillster for unfunded milestones.
                                </div>
                                <p>How much do you want to manually deduct from the Gigster's wallet to pay the Skillster?</p>
                                <div className="mb-3">
                                    <label className="form-label">Partial Amount ($)</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        value={partialAmount} 
                                        onChange={(e) => setPartialAmount(e.target.value)} 
                                        placeholder="Enter custom amount..."
                                        max={partialDetails?.available ? partialDetails.available / 100 : 0}
                                    />
                                    <small className="text-muted">Maximum available: ${(partialDetails?.available / 100 || 0).toFixed(2)}</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPartialModal(false)}>Cancel</button>
                                <button 
                                    type="button" 
                                    className="btn btn-success" 
                                    onClick={() => handleResolve('Process Partial Escrow', partialAmount)}
                                    disabled={resolving || !partialAmount || Number(partialAmount) <= 0 || Number(partialAmount) > (partialDetails?.available / 100)}
                                >
                                    {resolving ? 'Processing...' : 'Submit Partial Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DisputeDetails;
